import { Navigate, Outlet, useRoutes } from 'react-router-dom'
import { AppLayout, LoginLayout } from 'components/layout'
import { Lazy } from 'components/Loader'
import ErrorBoundary from 'components/ErrorBoundary'

function RouterRoutes() {
    return useRoutes([
        {
            path: '/',
            element: <ErrorBoundary><AppLayout /></ErrorBoundary>,
            children: [
                //dashboard
                { path: '/', element: <Lazy view="Home" /> },

                //profile
                { path: '/profile', element: <Lazy view="Auth/Profile" /> },

                // Transaction History
                { path: '/history', element: <Lazy view="Transaction/transactionHistory" /> },

                // A2 — Transactions extension
                {
                    path: '/transactions',
                    element: <Outlet />,
                    children: [
                        { path: 'incomplete', element: <Lazy view="Transaction/Incomplete" /> },
                        { path: 'funnel', element: <Lazy view="Transaction/Funnel" /> },
                        { path: 'stuck', element: <Lazy view="Transaction/Stuck" /> },
                        { path: 'dipository', element: <Lazy view="Dipository" /> },
                    ],
                },

                // A3 — SIP Management
                {
                    path: '/sip',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="Sip/Active" /> },
                        { path: 'incomplete', element: <Lazy view="Sip/Incomplete" /> },
                    ],
                },

                // Legacy redirects for old A4 bookmarks — declared BEFORE
                // the workbench so they match first.
                { path: '/screener/mf', element: <Navigate to="/screener/screen/mf" replace /> },
                { path: '/screener/equity', element: <Navigate to="/screener/screen/equity" replace /> },

                // Part C / SCREENER-0..5 — Market Intelligence Workbench.
                // Single shell with persistent left-rail nav + shared
                // FilterContext (basket + filters survive sub-route changes).
                {
                    path: '/screener',
                    element: <Lazy view="Screener/Insights/Layout" />,
                    children: [
                        // New Insights UI (mockup-driven)
                        { index: true, element: <Lazy view="Screener/Insights/Library" /> },
                        { path: 'results', element: <Lazy view="Screener/Insights/Results" /> },
                        { path: 'datasets', element: <Lazy view="Screener/Insights/Datasets" /> },
                        { path: 'status', element: <Lazy view="Screener/Insights/Status" /> },
                        // Legacy workbench sub-routes still reachable by URL
                        { path: 'screen/mf', element: <Lazy view="Screener/MFScreener" /> },
                        { path: 'screen/equity', element: <Lazy view="Screener/EquityScreener" /> },
                        // SCREENER-2 — Compare + Fund Overlap
                        { path: 'compare/mf', element: <Lazy view="Screener/Compare/MFCompare" /> },
                        { path: 'compare/equity', element: <Lazy view="Screener/Compare/EquityCompare" /> },
                        { path: 'overlap/schemes', element: <Lazy view="Screener/Overlap/BySchemes" /> },
                        { path: 'overlap/by-stock', element: <Lazy view="Screener/Overlap/ByStock" /> },
                        { path: 'overlap/most-held', element: <Lazy view="Screener/Overlap/MostHeld" /> },
                        // SCREENER-3 — Signals
                        { path: 'signals/consensus', element: <Lazy view="Screener/Signals/Consensus" /> },
                        { path: 'signals/shareholding', element: <Lazy view="Screener/Signals/Shareholding" /> },
                        { path: 'signals/52w', element: <Lazy view="Screener/Signals/FiftyTwoWeek" /> },
                        // SCREENER-4 — Returns Analytics
                        { path: 'returns/rolling', element: <Lazy view="Screener/Returns/Rolling" /> },
                        { path: 'returns/rolling-leaderboard', element: <Lazy view="Screener/Returns/RollingLeaderboard" /> },
                        { path: 'returns/sip-simulator', element: <Lazy view="Screener/Returns/SIPSimulator" /> },
                        { path: 'returns/sip-leaderboard', element: <Lazy view="Screener/Returns/SIPLeaderboard" /> },
                        // SCREENER-5 — Technicals + Peers + Aggregates
                        { path: 'technicals/moving-averages', element: <Lazy view="Screener/Technicals/MovingAverages" /> },
                        { path: 'technicals/ma-signals', element: <Lazy view="Screener/Technicals/MASignals" /> },
                        { path: 'peers/auto-detect', element: <Lazy view="Screener/Peers/AutoDetect" /> },
                        { path: 'aggregates/amc', element: <Lazy view="Screener/Aggregates/AMCLeaderboard" /> },
                        { path: 'aggregates/sector', element: <Lazy view="Screener/Aggregates/SectorPerformance" /> },
                        { path: 'aggregates/category', element: <Lazy view="Screener/Aggregates/CategoryComparison" /> },
                        { path: 'aggregates/user-flows', element: <Lazy view="Screener/Aggregates/UserFlows" /> },
                    ],
                },

                // A5 + D1 — Subscriptions: Revenue + lifecycle list views
                {
                    path: '/subscriptions',
                    element: <Outlet />,
                    children: [
                        { path: 'revenue', element: <Lazy view="Subscriptions/Revenue" /> },
                        // D1 — sidebar lifecycle list views (reuse /admin/mandates/ + /admin/tracking/incomplete/)
                        { path: 'active', element: <Lazy view="Subscriptions/Active" /> },
                        { path: 'incomplete', element: <Lazy view="Subscriptions/Incomplete" /> },
                        { path: 'expiring', element: <Lazy view="Subscriptions/Expiring" /> },
                        { path: 'churned', element: <Lazy view="Subscriptions/Churned" /> },
                    ],
                },
                {
                    path: '/payments',
                    element: <Outlet />,
                    children: [
                        { path: 'orders', element: <Lazy view="Payments/OrdersList" /> },
                        { path: 'mandates', element: <Lazy view="Payments/Mandates" /> },
                    ],
                },

                // A6 — Notifications
                {
                    path: '/notifications',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="Notifications/Stats" /> },
                        { path: 'stats', element: <Lazy view="Notifications/Stats" /> },
                        { path: 'templates', element: <Lazy view="Notifications/Templates" /> },
                        { path: 'broadcast', element: <Lazy view="Notifications/Broadcast" /> },
                    ],
                },

                // A8 — Reconciliation
                { path: '/reconciliation', element: <Lazy view="Reconciliation" /> },

                {path: '/integrity-results', element: <Lazy view="Integrityresults" /> },

                // D2 — Data Pipeline (Sync Status / Data Quality / Holiday Calendar)
                {
                    path: '/pipeline',
                    element: <Outlet />,
                    children: [
                        { path: 'sync', element: <Lazy view="Pipeline/Sync" /> },
                        { path: 'quality', element: <Lazy view="Pipeline/Quality" /> },
                        { path: 'holidays', element: <Lazy view="Pipeline/Holidays" /> },
                    ],
                },

                // A9 — Audit log inspectors (WebhookLog + GatewayApiLog) + D3 Activity Log
                {
                    path: '/audit',
                    element: <Outlet />,
                    children: [
                        { path: 'webhooks', element: <Lazy view="Audit/WebhookLogs" /> },
                        { path: 'gateway', element: <Lazy view="Audit/GatewayLogs" /> },
                        // D3 — admin-wide UserActivityLog feed.
                        { path: 'activity', element: <Lazy view="Audit/Activity" /> },
                    ],
                },

                //Roles
                {
                    path: '/roles',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="Roles" /> },
                        { path: 'create', element: <Lazy view="Roles/create" /> },
                        { path: ':id/edit', element: <Lazy view="Roles/create" /> },
                    ],
                },

                //Assign Permissions
                {
                    path: '/assign-permissions',
                    element: <Outlet />,
                    children: [{ path: '', element: <Lazy view="Permissions" /> }],
                },

                //user
                {
                    path: '/users',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="Users" /> },
                        { path: 'create', element: <Lazy view="Users/create" /> },
                        { path: ':id/edit', element: <Lazy view="Users/create" /> },
                    ],
                },
                {
                    path: '/live-users',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="Users/liveUsers" /> },
                        { path: 'create', element: <Lazy view="Users/create" /> },
                    ],
                },

                // Beta Invites — nested under User Management in the sidebar
                {
                    path: '/beta-invites',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="BetaInvites" /> },
                    ],
                },

                //Plans
                {
                    path: '/plans',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="Plans" /> },
                        { path: 'create', element: <Lazy view="Plans/create" /> },
                        { path: ':id/edit', element: <Lazy view="Plans/create" /> },
                    ],
                },

                //Coupons
                {
                    path: '/coupons',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="Coupon" /> },
                        { path: 'create', element: <Lazy view="Coupon/create" /> },
                        { path: ':id/edit', element: <Lazy view="Coupon/create" /> },
                    ],
                },

                // Questionnaires
                {
                    path: '/questionnaires',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="Risk-Questions/Questionnaires" /> },
                        { path: 'create', element: <Lazy view="Risk-Questions/Questionnaires/create" /> },
                        { path: ':id/edit', element: <Lazy view="Risk-Questions/Questionnaires/create" /> },
                    ],
                },

                // Questions
                {
                    path: '/questions',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="Risk-Questions/Questions" /> },
                        { path: 'create', element: <Lazy view="Risk-Questions/Questions/create" /> },
                        { path: ':id/edit', element: <Lazy view="Risk-Questions/Questions/create" /> },
                        { path: 'create/options/:qid', element: <Lazy view="Risk-Questions/Questions/addOptions" /> },
                    ],
                },

                // Options
                {
                    path: '/options',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="Risk-Questions/QuestionsOptions" /> },
                        { path: 'create', element: <Lazy view="Risk-Questions/QuestionsOptions/create" /> },
                        { path: ':id/edit', element: <Lazy view="Risk-Questions/QuestionsOptions/create" /> },
                    ],
                },

                // Risk Category
                {
                    path: '/risk-category',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="Risk-Questions/RiskCategory" /> },
                        { path: 'create', element: <Lazy view="Risk-Questions/RiskCategory/create" /> },
                        { path: ':id/edit', element: <Lazy view="Risk-Questions/RiskCategory/create" /> },
                    ],
                },

                // App Versions
                {
                    path: '/app-versions',
                    element: <Outlet />,
                    children: [
                        { path: '', element: <Lazy view="AppVersion" /> },
                    ],
                },
            ],
        },
        {
            path: '/',
            element: <ErrorBoundary><LoginLayout /></ErrorBoundary>,
            children: [
                { path: '/login', element: <Lazy view="Auth/Login" /> },
                { path: '/forgot-password', element: <Lazy view="Auth/ForgotPassword" /> },
                { path: '/admin-user/reset-password/:uid/:token', element: <Lazy view="Auth/ResetPassword" /> },
            ],
        },
        { path: 'error', element: <Lazy view="Error" /> },
        { path: '*', element: <Navigate to="/error" replace /> },
    ])
}

export default function Router() {
    return <RouterRoutes />
}
